import { registerInstrumentations } from '@opentelemetry/instrumentation';
import * as otelAPI from '@opentelemetry/api';
import * as otelLogsAPI from '@opentelemetry/api-logs';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  LoggerProvider,
  SimpleLogRecordProcessor,
} from '@opentelemetry/sdk-logs';
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';

// Configuration from environment variables
const tracesEndpoint =
  process.env.REACT_APP_OTEL_TRACES_ENDPOINT ||
  'http://localhost:4318/v1/traces';
const metricsEndpoint =
  process.env.REACT_APP_OTEL_METRICS_ENDPOINT ||
  'http://localhost:4318/v1/metrics';
const logsEndpoint =
  process.env.REACT_APP_OTEL_LOGS_ENDPOINT || 'http://localhost:4318/v1/logs';
const serviceName = process.env.REACT_APP_SERVICE_NAME || 'bot-admin-ui';
const serviceVersion = process.env.REACT_APP_SERVICE_VERSION || '0.1.0';
const environment = process.env.REACT_APP_ENVIRONMENT || 'localhost';

/**
 * Initializes OpenTelemetry tracing for the React application
 * Instruments document load, fetch requests, XHR, and user interactions
 */
export function initializeTelemetry() {
  // Create resource with service metadata
  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: serviceName,
    [ATTR_SERVICE_VERSION]: serviceVersion,
    environment,
  });

  // Set up tracing
  const traceExporter = new OTLPTraceExporter({
    url: tracesEndpoint,
  });

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(traceExporter, {
        scheduledDelayMillis: 500,
      }),
    ],
  });

  // Set up metrics
  const metricExporter = new OTLPMetricExporter({
    url: metricsEndpoint,
  });

  const metricReader = new PeriodicExportingMetricReader({
    exporter: metricExporter,
    exportIntervalMillis: 10000, // Export every 10 seconds
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [metricReader],
  });

  otelAPI.metrics.setGlobalMeterProvider(meterProvider);

  // Set up logging
  const logExporter = new OTLPLogExporter({
    url: logsEndpoint,
  });

  const loggerProvider = new LoggerProvider({
    resource,
    processors: [new SimpleLogRecordProcessor(logExporter)],
  });
  // Register with the global API
  const contextManager = new ZoneContextManager();

  tracerProvider.register({
    contextManager,
    propagator: new CompositePropagator({
      propagators: [
        new W3CBaggagePropagator(),
        new W3CTraceContextPropagator(),
      ],
    }),
  });

  otelLogsAPI.logs.setGlobalLoggerProvider(loggerProvider);

  // Register instrumentations
  registerInstrumentations({
    tracerProvider,
    meterProvider,
    loggerProvider,
    instrumentations: [
      // Traces document load events (page load performance)
      new DocumentLoadInstrumentation(),

      // Traces fetch API calls
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: /.*/,
        clearTimingResources: true,
        applyCustomAttributesOnSpan: (span, request, result) => {
          if (request instanceof Request) {
            span.setAttribute('http.url', request.url);
            span.setAttribute('http.method', request.method);
          }
          if (result instanceof Response) {
            span.setAttribute('http.status_code', result.status);
            span.setAttribute('http.response_type', result.type);
          }
        },
      }),

      // Traces XMLHttpRequest calls (for axios and other libraries)
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: /.*/,
        applyCustomAttributesOnSpan: (span, xhr) => {
          span.setAttribute('http.url', xhr.responseURL);
          span.setAttribute('http.status_code', xhr.status);
        },
      }),

      // Traces user interactions (clicks)
      new UserInteractionInstrumentation({
        eventNames: ['click', 'touchstart', 'submit', 'change'],
      }),
    ],
  });
}
